import type { QBExpense } from '../types';
import { extractCardLastFour } from './cardExpenseMatch';
import { isSharedProjectCardAccount } from './sequenceProjectCard';

export function isSequencePlaceholderExpense(
  expense: { id: string; quickbooksId?: string | null },
): boolean {
  if (expense.id.startsWith('seq-') || expense.id.startsWith('sto-')) return true;
  return String(expense.quickbooksId || '').startsWith('sequence:');
}

export function expenseDedupeKey(expense: QBExpense): string | null {
  const seq = typeof expense.sequenceTransactionId === 'string' ? expense.sequenceTransactionId.trim() : '';
  if (seq) return `seq:${seq}`;

  const amountCents = Math.round((Number(expense.totalAmount) || 0) * 100);
  const date = expense.txnDate || '';
  const card = expense.cardLastFour || '';
  if (amountCents > 0 && date) return `tx:${date}:${amountCents}:${card}`;
  return null;
}

export function pickPreferredDuplicate<T extends QBExpense & { id: string }>(a: T, b: T): T {
  const aPlaceholder = isSequencePlaceholderExpense(a);
  const bPlaceholder = isSequencePlaceholderExpense(b);
  if (aPlaceholder && !bPlaceholder) return b;
  if (!aPlaceholder && bPlaceholder) return a;

  if (a.qbWriteBackStatus === 'synced' && b.qbWriteBackStatus !== 'synced') return a;
  if (b.qbWriteBackStatus === 'synced' && a.qbWriteBackStatus !== 'synced') return b;

  const aRealQb = /^\d+$/.test(String(a.quickbooksId || ''));
  const bRealQb = /^\d+$/.test(String(b.quickbooksId || ''));
  if (aRealQb && !bRealQb) return a;
  if (bRealQb && !aRealQb) return b;

  const aAt = expenseSortTime(a);
  const bAt = expenseSortTime(b);
  return aAt >= bAt ? a : b;
}

function expenseSortTime(expense: QBExpense): number {
  const cat = expense.categorizedAt as Date | { toDate?: () => Date } | undefined;
  if (cat instanceof Date) return cat.getTime();
  if (cat && typeof cat === 'object' && typeof cat.toDate === 'function') {
    return cat.toDate().getTime();
  }
  return 0;
}

function parseTxnDay(txnDate: string | undefined): number | null {
  if (!txnDate) return null;
  const d = new Date(`${String(txnDate).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}

function cardDigits(expense: QBExpense): string {
  return expense.cardLastFour || extractCardLastFour(expense.bankAccountName) || '';
}

/** Sequence placeholder + QuickBooks purchase of the same physical-card charge. */
export function areSequenceQbTwins(
  a: QBExpense & { id: string },
  b: QBExpense & { id: string },
): boolean {
  const aPh = isSequencePlaceholderExpense(a);
  const bPh = isSequencePlaceholderExpense(b);
  if (aPh === bPh) return false;

  const seqA = typeof a.sequenceTransactionId === 'string' ? a.sequenceTransactionId.trim() : '';
  const seqB = typeof b.sequenceTransactionId === 'string' ? b.sequenceTransactionId.trim() : '';
  if (seqA && (b.id === `seq-${seqA}` || b.id === `sto-${seqA}`)) return true;
  if (seqB && (a.id === `seq-${seqB}` || a.id === `sto-${seqB}`)) return true;
  if (seqA && seqB && seqA !== seqB) return false;

  const amountA = Math.round((Number(a.totalAmount) || 0) * 100);
  const amountB = Math.round((Number(b.totalAmount) || 0) * 100);
  if (amountA <= 0 || amountA !== amountB) return false;

  const dayA = parseTxnDay(a.txnDate);
  const dayB = parseTxnDay(b.txnDate);
  if (dayA == null || dayB == null) return false;
  if (Math.abs(dayA - dayB) > 2 * 24 * 60 * 60 * 1000) return false;

  if (a.assignedUserId && b.assignedUserId && a.assignedUserId !== b.assignedUserId) return false;

  const cardA = cardDigits(a);
  const cardB = cardDigits(b);
  if (cardA && cardB && cardA !== cardB) {
    const shared =
      isSharedProjectCardAccount(a.bankAccountName)
      || isSharedProjectCardAccount(b.bankAccountName)
      || isSharedProjectCardAccount(cardA)
      || isSharedProjectCardAccount(cardB);
    if (!shared) return false;
  }
  return true;
}

function collapseSequenceQbTwins<T extends QBExpense & { id: string }>(rows: T[]): T[] {
  const parent = rows.map((_, i) => i);
  const find = (i: number): number => {
    let cur = i;
    while (parent[cur] !== cur) cur = parent[cur];
    return cur;
  };
  for (let i = 0; i < rows.length; i += 1) {
    for (let j = i + 1; j < rows.length; j += 1) {
      if (!areSequenceQbTwins(rows[i], rows[j])) continue;
      const a = find(i);
      const b = find(j);
      if (a !== b) parent[b] = a;
    }
  }
  const winners = new Map<number, T>();
  rows.forEach((row, i) => {
    const root = find(i);
    const prev = winners.get(root);
    winners.set(root, prev ? pickPreferredDuplicate(prev, row) : row);
  });
  return Array.from(winners.values());
}

/** One row per card charge — prefer the linked QuickBooks purchase over Sequence placeholders. */
export function dedupeCardExpenses<T extends QBExpense & { id: string }>(rows: T[]): T[] {
  const byKey = new Map<string, T>();
  const withoutKey: T[] = [];

  for (const row of rows) {
    const key = expenseDedupeKey(row);
    if (!key) {
      withoutKey.push(row);
      continue;
    }
    const prev = byKey.get(key);
    byKey.set(key, prev ? pickPreferredDuplicate(prev, row) : row);
  }

  return collapseSequenceQbTwins([...Array.from(byKey.values()), ...withoutKey]);
}
